import React, { createContext, useContext, useState, useEffect } from 'react';
import { Project } from '../definition';

interface ProjectContextType {
    projects: Project[];
    addProject: (project: Omit<Project, 'id'>) => void;
    updateProject: (id: string, project: Partial<Project>) => void;
    deleteProject: (id: string) => void;
    selectedProjectId: string | null;
    setSelectedProjectId: (id: string | null) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [projects, setProjects] = useState<Project[]>(() => {
        const saved = localStorage.getItem('calendar_projects');
        return saved ? JSON.parse(saved) : [{
            id: 'default',
            name: 'Project Nebula',
            vision: 'The future of task management',
            objective: 'Organize everything',
            priority: 'high',
            color: 'bg-emerald-50 text-emerald-700 border-l-4 border-l-emerald-400'
        }];
    });

    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(projects[0]?.id || null);

    useEffect(() => {
        localStorage.setItem('calendar_projects', JSON.stringify(projects));
    }, [projects]);

    const addProject = (project: Omit<Project, 'id'>) => {
        const newProject = {
            ...project,
            id: Math.random().toString(36).substring(2, 9)
        };
        setProjects(prev => [...prev, newProject]);
    };

    const updateProject = (id: string, updatedFields: Partial<Project>) => {
        setProjects(prev => prev.map(p => p.id === id ? { ...p, ...updatedFields } : p));
    };

    const deleteProject = (id: string) => {
        setProjects(prev => prev.filter(p => p.id !== id));
        if (selectedProjectId === id) {
            setSelectedProjectId(projects.find(p => p.id !== id)?.id || null);
        }
    };

    return (
        <ProjectContext.Provider value={{ 
            projects, 
            addProject, 
            updateProject, 
            deleteProject, 
            selectedProjectId, 
            setSelectedProjectId 
        }}>
            {children}
        </ProjectContext.Provider>
    );
};

export const useProjectContext = () => {
    const context = useContext(ProjectContext);
    if (!context) {
        throw new Error('useProjectContext must be used within a ProjectProvider');
    }
    return context;
};
